import { Global } from "../utils/Global";

interface BasicButtonConfig {
  textureKey: string;
  frameKey?: string;
}

export abstract class BasicButton extends Phaser.GameObjects.Container {
  abstract handlePointerDown(): void;
  abstract handlePointerOver(): void;
  abstract handlePointerUp(): void;
  abstract handlePointerOut(): void;

  protected disabled: boolean = false;
  protected pointerOver: boolean = false;

  protected background: Phaser.GameObjects.NineSlice;
  protected text: Phaser.GameObjects.BitmapText;

  constructor(
    scene: Phaser.Scene,
    buttonText: string,
    private readonly defaultTexture: BasicButtonConfig,
    private readonly hoverTexture?: BasicButtonConfig,
    private readonly pressedTexture?: BasicButtonConfig,
    private readonly disabledTexture?: BasicButtonConfig,
  ) {
    super(scene, 0, 0);

    this.background = new Phaser.GameObjects.NineSlice(
      scene,
      0,
      0,
      defaultTexture.textureKey,
      defaultTexture.frameKey,
      32,
      32,
      7,
      7,
      7,
      7,
    ).setInteractive();
    this.add(this.background);

    this.text = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      buttonText,
    ).setOrigin(0.5);
    this.add(this.text);

    this.background.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_OVER,
      this.onPointerOver,
      this,
    );
    this.background.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_OUT,
      this.onPointerOut,
      this,
    );
    this.background.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN,
      this.onPointerDown,
      this,
    );
    this.background.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_UP,
      this.onPointerUp,
      this,
    );
  }

  Resize(width: number, height: number): void {
    this.setSize(width, height);
    this.background.setSize(width, height);

    if (this.text) {
      this.text.setFontSize(
        Math.max(Global.minFontSize, Math.min(width, height) * 0.5),
      );
    }
  }

  SetText(text: string): void {
    this.text.setText(text);
  }

  SetFontSize(fontSize: number): void {
    this.text.setFontSize(fontSize);
  }

  ResizeToFitText(): void {
    const fontSize = Global.minFontSize;
    const padding = fontSize * 0.8;

    // Calculate required width based on text
    const textWidth = this.text.displayWidth;
    const textHeight = this.text.displayHeight;

    // Set minimum button dimensions
    const buttonWidth = Math.max(textWidth + padding * 2, fontSize * 5); // Minimum width
    const buttonHeight = Math.max(textHeight + padding, fontSize * 2); // Minimum height

    this.Resize(buttonWidth, buttonHeight);
  }

  public DisableButton(): void {
    this.disabled = true;
    if (this.disabledTexture) {
      this.background.setTexture(
        this.disabledTexture.textureKey,
        this.disabledTexture.frameKey,
      );
    }
  }

  public EnableButton(): void {
    this.disabled = false;
    if (this.pointerOver && this.hoverTexture) {
      this.background.setTexture(
        this.hoverTexture.textureKey,
        this.hoverTexture.frameKey,
      );
    } else {
      this.background.setTexture(
        this.defaultTexture.textureKey,
        this.defaultTexture.frameKey,
      );
    }
  }

  private onPointerDown(): void {
    if (!this.disabled && this.pressedTexture) {
      this.background.setTexture(
        this.pressedTexture.textureKey,
        this.pressedTexture.frameKey,
      );
    }
    this.handlePointerDown();
  }

  private onPointerUp(): void {
    if (!this.disabled) {
      if (this.pointerOver && this.hoverTexture) {
        this.background.setTexture(
          this.hoverTexture.textureKey,
          this.hoverTexture.frameKey,
        );
      } else {
        this.background.setTexture(
          this.defaultTexture.textureKey,
          this.defaultTexture.frameKey,
        );
      }
    }
    this.handlePointerUp();
  }

  private onPointerOver(): void {
    this.pointerOver = true;
    if (!this.disabled && this.hoverTexture) {
      this.background.setTexture(
        this.hoverTexture.textureKey,
        this.hoverTexture.frameKey,
      );
    }
    this.handlePointerOver();
  }

  private onPointerOut(): void {
    this.pointerOver = false;
    if (!this.disabled) {
      this.background.setTexture(
        this.defaultTexture.textureKey,
        this.defaultTexture.frameKey,
      );
    }
    this.handlePointerOut();
  }
}
